import { useCallback, useEffect, useMemo, useState } from 'react'
import { withBase } from '../config/api'
import {
  Box, Paper, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel,
  Toolbar, TextField, MenuItem, Select, FormControl,
  InputLabel, Button, Stack, TablePagination, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import EditButton from '../components/EditButtonModal'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

function numberOrZero(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0
  if (typeof v === 'string') {
    const cleaned = v.replace(/[^\d,.-]/g, '').replace(',', '.')
    const n = parseFloat(cleaned)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

function parseTotal(r) {
  return numberOrZero(r.total_price ?? r.totalPrice ?? 0)
}

function parseUnits(r) {
  return numberOrZero(r.units ?? 0)
}

function formatCurrency(value) {
  if (!Number.isFinite(value)) return '—'
  try {
    return new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR' }).format(value)
  } catch {
    return `${value} €`
  }
}

function getMembershipType(r) {
  return (
    r?.user?.membership?.membershipType?.type ||
    r?.membership?.membershipType?.type ||
    r?.user?.membership?.type ||
    r?.membership_type ||
    '—'
  )
}

const STATUS_OPTS = ['pending', 'processed', 'manual_check', 'rejected']
const FUEL_OPTS = ['fuel', 'electric']

function SortableHeaderCell({ col, orderBy, order, handleSort, colWidths, handleMouseDown }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: col.key })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: colWidths[col.key] || 150,
    minWidth: colWidths[col.key] || 150,
    maxWidth: colWidths[col.key] || 150,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: '#c8e1e1',
    fontWeight: 'bold'
  }

  return (
    <TableCell ref={setNodeRef} style={style} sortDirection={orderBy === col.key ? order : false}>
      <span {...attributes} {...listeners} style={{ cursor: 'grab', marginRight: 6, display: 'inline-block', color: '#999' }}>⠿</span>
      <TableSortLabel
        active={orderBy === col.key}
        direction={orderBy === col.key ? order : 'asc'}
        onClick={() => handleSort(col.key)}
      >
        {col.label}
      </TableSortLabel>
      <div
        onMouseDown={(e) => handleMouseDown(e, col.key)}
        style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '5px', cursor: 'col-resize', zIndex: 1 }}
      />
    </TableCell>
  )
}

export default function AdminReceipts() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selected, setSelected] = useState(null)

  const [filters, setFilters] = useState({ q: '', status: '', fuelType: '', userId: '', from: '', to: '' })
  const [orderBy, setOrderBy] = useState('userId')
  const [order, setOrder] = useState('asc')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [total, setTotal] = useState(0)

  const [columns, setColumns] = useState([
    { key: 'status', label: 'Stav' },
    { key: 'id', label: 'ID' },
    { key: 'userId', label: 'User' },
    { key: 'membershipType', label: 'Členstvo typ' },
    { key: 'store_name', label: 'Čerp. stanica' },
    { key: 'store_address', label: 'Adresa' },
    { key: 'ico', label: 'IČO' },
    { key: 'dic', label: 'DIČ' },
    { key: 'receipt_number', label: 'Číslo bločku' },
    { key: 'transaction_id', label: 'Transakcia' },
    { key: 'currency', label: 'Mena' },
    { key: 'uid', label: 'UID' },
    { key: 'fuel_name', label: 'Názov paliva' },
    { key: 'payment_type', label: 'Platba' },
    { key: 'unit', label: 'Jednotka' },
    { key: 'unit_price', label: 'Cena/jednotku' },
    { key: 'vat_rate', label: 'DPH (%)' },
    { key: 'date', label: 'Dátum' },
    { key: 'total_price', label: 'Suma' },
    { key: 'units', label: 'Množstvo' },
    { key: 'fuel_type', label: 'Typ paliva' },
    { key: 'manual_check_reason', label: 'Dôvod pre manual check' },
    { key: 'vehicle_id', label: 'Vozidlo ID' },
    { key: 'createdAt', label: 'Vytvorené' },
    { key: 'updatedAt', label: 'Upravené' },
  ])

  const [colWidths, setColWidths] = useState({
    status: 115,
    id: 60,
    userId: 75,
    membershipType: 130,
    currency: 80,
    date: 100,
    total_price: 85,
    units: 115,
    vehicle_id: 115,
    manual_check_reason: 220,
  })

  const sensors = useSensors(useSensor(PointerSensor))

  const queryString = useMemo(() => {
    const p = new URLSearchParams()
    if (filters.q.trim()) p.set('q', filters.q.trim())
    if (filters.status) p.set('status', filters.status)
    if (filters.fuelType) p.set('fuel_type', filters.fuelType)
    if (filters.userId) p.set('userId', filters.userId)
    if (filters.from) p.set('from', filters.from)
    if (filters.to) p.set('to', filters.to)
    p.set('sort', `${orderBy},${order}`)
    p.set('limit', String(rowsPerPage))
    p.set('offset', String(page * rowsPerPage))
    return p.toString()
  }, [filters, orderBy, order, rowsPerPage, page])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth(withBase(`/admin/receipts?${queryString}`))

      if (!res.ok) {
        const err = await res.json().catch(() => null)
        throw new Error(err?.message || `HTTP ${res.status}`)
      }

      const payload = await res.json()
      const list = Array.isArray(payload?.rows) ? payload.rows : []

      setRows(list)
      setTotal(Number(payload?.count ?? list.length) || 0)
    } catch (e) {
      setError(e.message || 'Chyba pri načítaní')
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [queryString])

  useEffect(() => { load() }, [load])

  const handleSort = (col) => {
    if (orderBy === col) setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
    else { setOrderBy(col); setOrder('asc') }
  }

  const handleMouseDown = (e, key) => {
    const startX = e.clientX
    const startWidth = colWidths[key] || 150

    const onMouseMove = (e) => {
      const delta = e.clientX - startX
      const newWidth = Math.max(50, startWidth + delta)
      setColWidths(prev => ({ ...prev, [key]: newWidth }))
    }

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  const handleDragEnd = (event) => {
    const { active, over } = event

    if (active.id !== over.id) {
      const oldIndex = columns.findIndex(c => c.key === active.id)
      const newIndex = columns.findIndex(c => c.key === over.id)
      const reordered = [...columns]
      const [moved] = reordered.splice(oldIndex, 1)

      reordered.splice(newIndex, 0, moved)
      setColumns(reordered)
    }
  }

  const resetFilters = () => setFilters({ q: '', status: '', fuelType: '', userId: '', from: '', to: '' })

  return (
    <div
      style={{
        background: '#ededed',
        padding: 12,
        border: '1.5px solid #107c6b',
        borderRadius: 10,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          marginBottom: 1
        }}
      >
        <h3
          style={{
            fontSize: 16,
            margin: 0
          }}
        >
          Zoznam účteniek
        </h3>

        <Typography
          sx={{
            fontSize: 13,
            opacity: 0.7
          }}
        >
          Záznamov: {total}
        </Typography>
      </Box>

      <Toolbar
        disableGutters
        sx={{
          minHeight: '42px !important',
          marginBottom: 1
        }}
      >
        <Stack direction="row" spacing={2} sx={{ width: '100%', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Hľadať"
            value={filters.q}
            onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
            sx={{
              minWidth: 240,
              '& .MuiInputBase-root': {
                fontSize: 13
              },
              '& .MuiInputLabel-root': {
                fontSize: 13
              }
            }}
          />

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Stav</InputLabel>
            <Select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
              <MenuItem value=""><em>—</em></MenuItem>
              {STATUS_OPTS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Typ paliva</InputLabel>
            <Select value={filters.fuelType} onChange={e => setFilters(f => ({ ...f, fuelType: e.target.value }))}>
              <MenuItem value=""><em>—</em></MenuItem>
              {FUEL_OPTS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField size="small" label="User ID" value={filters.userId} onChange={e => setFilters(f => ({ ...f, userId: e.target.value }))} sx={{ width: 120 }} />
          <TextField size="small" type="date" label="Od" value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} InputLabelProps={{ shrink: true }} />
          <TextField size="small" type="date" label="Do" value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} InputLabelProps={{ shrink: true }} />

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={resetFilters}>Reset</Button>
            <Button variant="contained" onClick={load} disabled={loading}>Obnoviť</Button>
          </Stack>
        </Stack>
      </Toolbar>

      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          border: '1px solid #c7c7c7',
          borderRadius: '10px',
          boxShadow: 'none'
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            '& thead th': {
              fontWeight: 'bold',
              backgroundColor: '#c8e1e1',
              whiteSpace: 'nowrap',
              fontSize: 12,
              padding: '6px 8px'
            },

            '& tbody td': {
              fontSize: 12,
              padding: '5px 8px',
              whiteSpace: 'nowrap',
              height: 29,
              boxSizing: 'border-box'
            },

            '& tbody .MuiChip-root': {
              height: 20
            },

            '& tbody .MuiButton-root': {
              minHeight: 20,
              height: 20,
              padding: '0 8px'
            },

            '& .MuiTableSortLabel-root': {
              fontSize: 12
            }
          }}
        >
          <TableHead>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={columns.map(c => c.key)} strategy={horizontalListSortingStrategy}>
                <TableRow>
                  <TableCell>Upraviť</TableCell>

                  {columns.map(col => (
                    <SortableHeaderCell
                      key={col.key}
                      col={col}
                      orderBy={orderBy}
                      order={order}
                      handleSort={handleSort}
                      colWidths={colWidths}
                      handleMouseDown={handleMouseDown}
                    />
                  ))}
                </TableRow>
              </SortableContext>
            </DndContext>
          </TableHead>

          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} hover>
                <TableCell>
                  <EditButton onClick={() => setSelected(r)} />
                </TableCell>

                {columns.map(col => (
                  <TableCell
                    key={col.key}
                    style={{
                      width: colWidths[col.key] || 150,
                      minWidth: colWidths[col.key] || 150,
                      maxWidth: colWidths[col.key] || 150,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {(() => {
                      switch (col.key) {
                        case 'membershipType': return getMembershipType(r)
                        case 'date': return (r.date || '').toString().split('T')[0] || '—'
                        case 'total_price': return formatCurrency(parseTotal(r))
                        case 'units': return parseUnits(r)
                        case 'fuel_type': return r.fuel_type ? <Chip size="small" label={r.fuel_type} /> : '—'

                        case 'status':
                          if (!r.status) return '—'

                          const statusColors = {
                            processed: { backgroundColor: '#A5D6A7', color: '#1B5E20' },
                            manual_check: { backgroundColor: '#EF9A9A', color: '#B71C1C' }
                          }

                          return <Chip size="small" label={r.status} sx={statusColors[r.status] || {}} />

                        default: return r[col.key] || '—'
                      }
                    })()}
                  </TableCell>
                ))}
              </TableRow>
            ))}

            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {error ? `Chyba: ${error}` : 'Žiadne dáta'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Na stránku"
        />
      </Box>

      <EditDialog
        open={!!selected}
        onClose={() => setSelected(null)}
        receipt={selected}
        onSaved={(updated) => {
          setSelected(null)
          setRows(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r))
        }}
      />
    </div>
  )
}

function EditDialog({ open, onClose, receipt, onSaved }) {
  const [values, setValues] = useState({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (receipt) {
      setValues({
        store_name: receipt.store_name || '',
        store_address: receipt.store_address || '',
        ico: receipt.ico || '',
        dic: receipt.dic || '',
        receipt_number: receipt.receipt_number || '',
        transaction_id: receipt.transaction_id || '',
        currency: receipt.currency || 'EUR',
        uid: receipt.uid || '',
        fuel_name: receipt.fuel_name || '',
        payment_type: receipt.payment_type || '',
        unit: receipt.unit || '',
        unit_price: receipt.unit_price || '',
        vat_rate: receipt.vat_rate || '',
        date: (receipt.date || '').toString().split('T')[0] || '',
        total_price: parseTotal(receipt),
        units: parseUnits(receipt),
        fuel_type: receipt.fuel_type || 'fuel',
        status: receipt.status || 'pending',
        manual_check_reason: receipt.manual_check_reason || '',
        vehicle_id: receipt.vehicle_id || '',
      })
    }
  }, [receipt])

  const patch = (k, v) => setValues(prev => ({ ...prev, [k]: v }))

  const handleSave = async () => {
    if (!receipt) return

    setSaving(true)
    setError(null)

    try {
      const body = {
        store_name: values.store_name || null,
        store_address: values.store_address || null,
        ico: values.ico || null,
        dic: values.dic || null,
        receipt_number: values.receipt_number || null,
        transaction_id: values.transaction_id || null,
        currency: values.currency || 'EUR',
        uid: values.uid || null,
        fuel_name: values.fuel_name || null,
        payment_type: values.payment_type || null,
        unit: values.unit || null,
        unit_price: numberOrZero(values.unit_price),
        vat_rate: numberOrZero(values.vat_rate),
        date: values.date || null,
        total_price: numberOrZero(values.total_price),
        units: numberOrZero(values.units),
        fuel_type: values.fuel_type || 'fuel',
        status: values.status || 'pending',
        manual_check_reason: values.manual_check_reason || null,
        vehicle_id: values.vehicle_id || null,
      }

      const res = await fetchWithAuth(withBase(`/admin/receipts/${receipt.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      if (!res.ok) {
        const err = await res.json().catch(() => null)
        throw new Error(err?.message || `HTTP ${res.status}`)
      }

      const data = await res.json()
      onSaved?.(data)

    } catch (e) {
      setError(e.message || 'Chyba pri ukladaní')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Upraviť bloček #{receipt?.id}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Čerpacia stanica" value={values.store_name || ''} onChange={e => patch('store_name', e.target.value)} fullWidth />
          <TextField label="Adresa" value={values.store_address || ''} onChange={e => patch('store_address', e.target.value)} fullWidth />

          <Stack direction="row" spacing={2}>
            <TextField label="IČO" value={values.ico || ''} onChange={e => patch('ico', e.target.value)} />
            <TextField label="DIČ" value={values.dic || ''} onChange={e => patch('dic', e.target.value)} />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField label="Číslo bločku" value={values.receipt_number || ''} onChange={e => patch('receipt_number', e.target.value)} />
            <TextField label="Transakcia ID" value={values.transaction_id || ''} onChange={e => patch('transaction_id', e.target.value)} />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField label="Mena" value={values.currency || ''} onChange={e => patch('currency', e.target.value)} />
            <TextField label="UID" value={values.uid || ''} onChange={e => patch('uid', e.target.value)} />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField label="Názov paliva" value={values.fuel_name || ''} onChange={e => patch('fuel_name', e.target.value)} />
            <TextField label="Platba" value={values.payment_type || ''} onChange={e => patch('payment_type', e.target.value)} />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField label="Jednotka" value={values.unit || ''} onChange={e => patch('unit', e.target.value)} />
            <TextField label="Cena/jednotku" value={values.unit_price || ''} onChange={e => patch('unit_price', e.target.value)} />
            <TextField label="DPH (%)" value={values.vat_rate || ''} onChange={e => patch('vat_rate', e.target.value)} />
          </Stack>

          <TextField type="date" label="Dátum" value={values.date || ''} onChange={e => patch('date', e.target.value)} InputLabelProps={{ shrink: true }} />

          <Stack direction="row" spacing={2}>
            <TextField label="Suma (EUR)" value={values.total_price ?? ''} onChange={e => patch('total_price', e.target.value)} />
            <TextField label="Množstvo (l/kWh)" value={values.units ?? ''} onChange={e => patch('units', e.target.value)} />
          </Stack>

          <FormControl fullWidth>
            <InputLabel>Typ paliva</InputLabel>
            <Select value={values.fuel_type || 'fuel'} onChange={e => patch('fuel_type', e.target.value)}>
              {FUEL_OPTS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Stav</InputLabel>
            <Select value={values.status || 'pending'} onChange={e => patch('status', e.target.value)}>
              {STATUS_OPTS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField label="Dôvod manuálnej kontroly" value={values.manual_check_reason || ''} onChange={e => patch('manual_check_reason', e.target.value)} fullWidth />
          <TextField label="Vozidlo ID" value={values.vehicle_id || ''} onChange={e => patch('vehicle_id', e.target.value)} fullWidth />

          {error && <Typography color="error" variant="body2">{String(error)}</Typography>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Zrušiť</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>Uložiť</Button>
      </DialogActions>
    </Dialog>
  )
}